import {Space} from "antd";
import React, {FC, memo} from "react";
import moment from "moment";
import {red} from "@ant-design/colors";
import {FormikProps} from "formik";
import AlertCard from "../../../views/AlertCard/AlertCard";
import LayoutForm from "../../../views/LayoutForm/LayoutForm";
import {InputCollectionDate, InputCreateButton, InputMaxAmount, InputMaxMoneyAmount, InputStartEndRange} from "./InputFields";
import {CreateSepaXmlByTimeRangeInput} from "../../../generated/graphql";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import {buildTimeRangeInput} from "./buildTimeRangeInput";
import {XmlResponse} from "./CreateXmlModal";

type InputFormProps = {
    formikProps: FormikProps<CreateSepaXmlByTimeRangeInput>;
    currentDateIsBeforeEndDate: boolean;
    createSepaXmlByTimeRangeInput: CreateSepaXmlByTimeRangeInput;
    /** Meldet ein abgeschlossenes Eingabeereignis an den Preview-Koordinator im Modal. */
    onPreviewRequest: (values: CreateSepaXmlByTimeRangeInput) => void;
    setCreateSepaXmlByTimeRangeInput: (value: CreateSepaXmlByTimeRangeInput) => void;
    setXmlData: React.Dispatch<React.SetStateAction<XmlResponse | undefined>>;
    /** WRITE-Pfad: schreibt die Datei (saveFile:true) und lädt sie herunter. */
    loadXmlData: (updatedDates: CreateSepaXmlByTimeRangeInput) => Promise<void>;
    noPayments: boolean;
    paymentCount?: number;
    errors: Array<{errorTitle: string; errorMsg: string}> | undefined;
    loading?: boolean;
};

const InputForm: FC<InputFormProps> = ({
    formikProps,
    currentDateIsBeforeEndDate,
    createSepaXmlByTimeRangeInput,
    onPreviewRequest,
    setCreateSepaXmlByTimeRangeInput,
    setXmlData,
    loadXmlData,
    noPayments,
    paymentCount,
    errors,
    loading,
}) => {
    const {values} = formikProps;

    const maxAmount: number | undefined = isDefined(values.maxAmount) ? values.maxAmount : undefined;

    const saveAndDownload = async () => {
        // WICHTIG: Die geschriebene XML-Datei MUSS exakt dieselbe Datums-Serialisierung verwenden wie
        // die Vorschau (buildTimeRangeInput) — sonst weicht der Export vom angezeigten Vorschau-Ergebnis ab.
        // Früherer Bug (Prod fit-im-wasser 2026-06-02): hier wurde das Datum als blosses "YYYY-MM-DD"
        // gesendet. Der Server castet endDate dann zu 00:00 Uhr (Tagesanfang) statt zum lokalen Tagesende,
        // wodurch ALLE am Stichtag fälligen Raten aus dem Export fielen — die Vorschau (Tagesende) zeigte
        // sie an, die Datei enthielt sie nicht; bei ausschliesslich am Stichtag fälligen Raten schlug der
        // Export sogar mit "No scheduled payments found" fehl.
        const timeRangeInput = buildTimeRangeInput({
            startDate: values.startDate,
            endDate: values.endDate,
            collectionDate: values.collectionDate,
            saveFile: true,
            maxAmount,
            maxMoneyAmount: isDefined(values.maxMoneyAmount) ? values.maxMoneyAmount : undefined,
        });

        setCreateSepaXmlByTimeRangeInput(timeRangeInput);

        // Genau EIN Request pro bestätigtem Export (Issue #671) — auf das Ergebnis wird gewartet,
        // damit AntD den Bestätigungsdialog so lange im Loading-Zustand hält.
        await loadXmlData(timeRangeInput);

        setXmlData(undefined);
        setCreateSepaXmlByTimeRangeInput({
            collectionDate: undefined,
            endDate: undefined,
            startDate: undefined,
            saveFile: false,
            maxAmount: undefined,
        });
    };

    return (
        <Space direction="vertical" style={{width: "100%"}}>
            {currentDateIsBeforeEndDate && (
                <AlertCard
                    title={`Achtung! Der Fälligkeitszeitraum liegt in der Zukunft. (${moment(createSepaXmlByTimeRangeInput.endDate).format("DD.MM.YYYY")}) Bitte überprüfe, ob das gewünscht ist.`}
                    type="WARNING"
                    style={{
                        border: `2px solid ${red[4]}`,
                        width: "100%",
                        backgroundColor: red[1],
                    }}
                    showIcon={false}
                />
            )}
            <LayoutForm
                layout="vertical"
                size="small"
                style={{
                    display: "block",
                    alignItems: "baseline",
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gridGap: "10px",
                    }}
                >
                    <InputStartEndRange formikProps={formikProps} onPreviewRequest={onPreviewRequest} style={{gridColumn: "span 2"}} />
                    <InputCollectionDate formikProps={formikProps} onPreviewRequest={onPreviewRequest} />
                    <InputMaxAmount formikProps={formikProps} onPreviewRequest={onPreviewRequest} />
                    <InputMaxMoneyAmount formikProps={formikProps} onPreviewRequest={onPreviewRequest} />
                </div>
                <InputCreateButton noPayments={noPayments} errors={errors} saveAndDownload={saveAndDownload} paymentCount={paymentCount} loading={loading} />
            </LayoutForm>
        </Space>
    );
};

export default memo(InputForm);
